import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useCreateChain } from "../hooks/useCreateChain";
import {
  useStaticSystemsSearch,
  type SystemSearchResult,
} from "../hooks/useStaticSystems";
import { ACCESS_LABELS, ACCESS_TIERS, isJspaceClass } from "../types/chain";
import type { ChainAccess, ChainSummary } from "../types/chain";

interface CreateChainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownedRootSystemIds: Map<string, string>;
  onCreated: (chain: ChainSummary) => void;
}

export function CreateChainModal({
  open,
  onOpenChange,
  ownedRootSystemIds,
  onCreated,
}: CreateChainModalProps) {
  const auth = useAuth();
  const staticSystems = useStaticSystemsSearch();
  const createChain = useCreateChain();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SystemSearchResult | null>(null);
  const [name, setName] = useState("");
  const [access, setAccess] = useState<ChainAccess>("private");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelected(null);
      setName("");
      setAccess("private");
      setError(null);
      createChain.reset();
    }
  }, [open, createChain]);

  const results = useMemo(() => {
    const showResults = query.trim().length > 0 && (!selected || selected.name !== query);
    if (!showResults) return [];
    return staticSystems
      .search(query, 100)
      .filter((r) => isJspaceClass(r.class))
      .slice(0, 20);
  }, [query, selected, staticSystems]);

  const ownedConflictName = selected
    ? ownedRootSystemIds.get(selected.id) ?? null
    : null;

  const allianceName = auth.data?.alliance_name ?? null;
  const allianceId = auth.data?.alliance_id ?? null;
  const corporationName = auth.data?.corporation_name ?? null;
  const allianceDisabled = allianceId == null;

  const visibilityHelper: Record<ChainAccess, string> = {
    private: "Visible only to you",
    corp: corporationName
      ? `Visible to ${corporationName}`
      : "Visible to your corporation",
    alliance: allianceDisabled
      ? "You have no alliance"
      : allianceName
        ? `Visible to ${allianceName}`
        : "Visible to your alliance",
    public: "Visible to anyone signed in",
  };

  const trimmedName = name.trim();
  const submitDisabled =
    !selected ||
    trimmedName.length === 0 ||
    createChain.isPending ||
    (access === "alliance" && allianceDisabled);

  const handlePick = (r: SystemSearchResult) => {
    setSelected(r);
    setQuery(r.name);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || trimmedName.length === 0) return;
    setError(null);
    createChain.mutate(
      {
        name: trimmedName,
        root_system_id: Number(selected.id),
        access,
      },
      {
        onSuccess: (created) => onCreated(created),
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal__content" aria-describedby={undefined}>
          <Dialog.Title className="modal__title">
            <Plus size={16} aria-hidden /> New Chain
          </Dialog.Title>
          <form className="form modal__form" onSubmit={handleSubmit}>
            <div className="form__row">
              <label className="form__label" htmlFor="chain-root-system">
                Root system
              </label>
              <input
                id="chain-root-system"
                className="form__input"
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="Type to search…"
                autoComplete="off"
              />
              {selected && (
                <input type="hidden" name="root_system_id" value={selected.id} />
              )}
              {results.length > 0 && (
                <ul className="picker-results">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="picker-results__item"
                        onClick={() => handlePick(r)}
                      >
                        {r.name} ({r.class}
                        {r.region ? `, ${r.region}` : ""})
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form__row">
              <label className="form__label" htmlFor="chain-name">
                Name
              </label>
              <input
                id="chain-name"
                className="form__input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {ownedConflictName && (
                <p className="form__hint form__hint--warn">
                  You already have a chain rooted at {selected!.name}:{" "}
                  {ownedConflictName}
                </p>
              )}
            </div>

            <div className="form__row">
              <span className="form__label">Visibility</span>
              <div className="radio-group">
                {ACCESS_TIERS.map((tier) => {
                  const disabled = tier === "alliance" && allianceDisabled;
                  return (
                    <label
                      key={tier}
                      className={
                        "radio-group__option" +
                        (disabled ? " radio-group__option--disabled" : "")
                      }
                    >
                      <input
                        type="radio"
                        name="chain-access"
                        value={tier}
                        checked={access === tier}
                        disabled={disabled}
                        onChange={() => setAccess(tier)}
                      />
                      {ACCESS_LABELS[tier]}
                    </label>
                  );
                })}
              </div>
              <p className="form__hint">{visibilityHelper[access]}</p>
            </div>

            {error && <p className="form__hint form__hint--error">{error}</p>}

            <div className="modal__actions">
              <button
                type="button"
                className="modal__btn"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal__btn modal__btn--primary"
                disabled={submitDisabled}
              >
                {createChain.isPending ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
